import { getGratificationById } from 'api/v1/gratifications';
import {
  getConversationMessages,
  getConversationMetadata,
  getConversationUsage,
  postConversationMetadata,
} from 'api/v2/AI';
import { uploadFile } from 'api/v2/upload';
import type { CancelTokenSource } from 'axios';
import axios from 'axios';
import { Status } from 'components/molecules/FileUploadedList/FileUploaded.types';
import { useAiMetadata } from 'contexts/AIMetadataProvider';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useCustomDropzone from 'hooks/useCustomDropzone';
import { get } from 'lodash';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, type UseQueryResult } from 'react-query';
import type { ConversationMetadata } from 'types/ai.types';
import {
  AIRoleEnum,
  type AIUsage,
  type ConversationHistory,
} from 'types/ai.types';
import { AICacheKeys } from 'types/cacheKeys.types';
import { UploadTypesEnum } from 'types/uploadEnums.types';
import { globalyAcceptedFileUploaderTypes, maxFileSize } from 'utils/constants';
import { formatMetadata } from 'utils/gratifications/getMetaData';
import { gratificationsData } from 'utils/gratifications/gratificationsData';
import type { AIChatAttachment, AIChatAttachmentApi } from './AIChat.types';
import { getStoredAuthData } from 'utils/auth';

export const useGetGratificationById = (currentTipId: string) => {
  const { t } = useTranslation();
  return useQuery(
    [currentTipId],
    () => getGratificationById(currentTipId as string),
    {
      enabled: !!currentTipId,
      select: (tip) => {
        const _meta = formatMetadata(get(tip, 'metadata'));
        const gratifContent = gratificationsData[tip.gratificationNumber];
        const tipTextContent = get(gratifContent, 'helperContent', [])?.find(
          (comp) => !!comp.text,
        );
        const description = tipTextContent
          ? t(tipTextContent?.text as string, _meta)
          : '';
        const title = t(
          get(gratificationsData, [tip.gratificationNumber, 'text']),
          _meta,
        );
        return {
          ...tip,
          title,
          description,
          gratifContent: gratificationsData[tip.gratificationNumber],
        };
      },
    },
  );
};

export const useConversationQuery = (
  conversationId: string,
  onSuccess: (data: ConversationHistory) => void,
): UseQueryResult<ConversationHistory> =>
  useQuery(
    [AICacheKeys.ConversationMessages, conversationId],
    () => getConversationMessages(conversationId),
    {
      onSuccess,
      keepPreviousData: true,
      enabled: !!conversationId,
      cacheTime: 0,
    },
  );

export const useUsageQuery = (
  conversation: ConversationHistory | null,
  message: string,
) => {
  const { data: user } = useGetCurrentCustomerData();

  const isAccountableUser = user?.email.endsWith('@accountable.eu');
  const currentCount =
    conversation?.messages?.filter((m) => m.role === AIRoleEnum.user).length ||
    0;

  const defaultUsage: AIUsage = {
    reachedMaxCount: false,
    messageLengthExceeded: message?.length > 1500,
    currentCount,
    reachedMaxMonthlyCount: false,
    monthly_message_count: 0,
    monthly_message_limit: 50,
    conversation_message_limit: 5,
  };

  const usageQuery = useQuery(
    [AICacheKeys.ConversationUsage],
    getConversationUsage,
    {
      enabled: !!getStoredAuthData('access_token'),
      keepPreviousData: true,
      select: ({
        monthly_message_count = 0,
        monthly_message_limit = 50,
        conversation_message_limit = 5,
      }) => ({
        ...defaultUsage,
        reachedMaxCount: currentCount >= conversation_message_limit,
        reachedMaxMonthlyCount:
          !isAccountableUser && monthly_message_count >= monthly_message_limit,
        monthly_message_count,
        monthly_message_limit,
        conversation_message_limit,
      }),
    },
  );

  return { ...usageQuery, data: usageQuery.data || defaultUsage };
};

export const useConversationMetadata = (
  conversationId?: string,
  tempMetadata?: ConversationMetadata | null,
) => {
  const {
    data: { data: metadataItems = [] } = {},
    isLoading,
    refetch,
  } = useQuery(
    [AICacheKeys.ConversationMetadata, conversationId],
    () => getConversationMetadata(conversationId!),
    { enabled: !!conversationId?.length, keepPreviousData: false },
  );

  // concatenate tempMetadata if it provided
  if (tempMetadata) metadataItems.push(tempMetadata);

  return { metadataItems, isLoading, refetchMetadata: refetch };
};

export const useAIChatAttachment = (
  allowFileMetadata = false,
): AIChatAttachmentApi => {
  const [attachment, setAttachment] = useState<AIChatAttachment | null>(null);
  const uploadCancelToken = useRef<CancelTokenSource | null>(null);
  const { setMetadata } = useAiMetadata();

  const onUploadProgress = (progress: number) => {
    setAttachment((prev) =>
      prev ? { ...prev, progress: Math.round(progress) } : null,
    );
  };

  const uploadAttachment = async (file: File) => {
    const isPdf = file.type === 'application/pdf';

    const attachment: AIChatAttachment = {
      status: Status.uploading,
      type: isPdf ? 'pdf' : 'image',
      file,
      progress: 0,
    };

    try {
      uploadCancelToken.current = axios.CancelToken.source();
      setAttachment(attachment);

      const { key } = await uploadFile({
        file,
        category: UploadTypesEnum.conversationAttachment,
        onProgress: onUploadProgress,
        cancelToken: uploadCancelToken.current.token,
      });
      if (!uploadCancelToken.current) return;

      const metadata: ConversationMetadata = {
        documentType: attachment.type,
        data: {
          filePath: key,
          filename: key.split('/').pop(),
          originalFilename: file.name,
        },
      };

      const { data: saved_metadata } = await postConversationMetadata(metadata);

      if (!uploadCancelToken.current) return;

      setMetadata(saved_metadata, attachment.type);
      setAttachment({ ...attachment, status: Status.done });
    } catch (error) {
      if (!axios.isCancel(error)) {
        setAttachment({ ...attachment, status: Status.error });
      }
    }
  };

  const handleFileSelect = useCallback(
    async (files: File[]) => {
      if (!files?.length || !allowFileMetadata) return;
      await uploadAttachment(files[0]);
    },
    [allowFileMetadata],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useCustomDropzone(
    {
      onDropAccepted: handleFileSelect,
      accept: globalyAcceptedFileUploaderTypes,
      maxSize: maxFileSize,
      noClick: true,
      disabled: !allowFileMetadata,
    },
  );

  const remove = () => {
    if (uploadCancelToken.current) {
      uploadCancelToken.current.cancel();
      uploadCancelToken.current = null;
    }
    setAttachment(null);
  };

  return {
    attachment,
    enabled: allowFileMetadata,
    upload: uploadAttachment,
    open,
    remove,
    dropzoneProps: {
      root: getRootProps,
      input: getInputProps,
      isDragActive,
    },
  };
};
